import { useRatebooks, useRateManagement } from '@api/useFreight/useFreight'

import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { useUserSettings } from '@hooks/settings'
import { useQueryClient } from '@tanstack/react-query'
import type { ColDef, IFilter, RowClassParams } from 'ag-grid-community'
import type { GridApi } from 'ag-grid-community'
import { type MutableRefObject, useEffect, useMemo, useRef } from 'react'
import dayjs from 'dayjs'

import * as Renderers from '../Customer/cellRenderers'
import { DefaultTransporterRenderer } from '../components/DefaultTransporterRenderer'
import type { BookType, Ratebook } from '../../../../api/useFreight/types'
import type { RatebookType } from '../types'

export interface UseRatebooksStateReturn {
  gridAPIRef: React.RefObject<GridApi<Ratebook>>
  ratebooks: Ratebook[] | undefined
  isLoading: boolean
  updateSetting: (key: string, value: unknown) => void
  canEdit: boolean | undefined
  columnDefs: ColDef<Ratebook>[]
  agPropOverrides: Record<string, unknown>
  exportPointToPointRates: (ratebook?: Ratebook | { book_type: BookType }) => Promise<{ data: Blob }>
  importPointToPointRates: (file: FormData, ratebook?: Ratebook) => Promise<{ data: Blob }>
  exportBaseFreight: (ratebook?: Ratebook | { book_type: BookType }) => Promise<{ data: Blob }>
  importBaseFreight: (file: FormData, ratebook?: Ratebook) => Promise<{ data: Blob }>
  exportSurcharges: (ratebook?: Ratebook | { book_type: BookType }) => Promise<{ data: Blob }>
  importSurcharges: (file: FormData, ratebook?: Ratebook) => Promise<{ data: Blob }>
}

export function useRatebooksState(type: RatebookType): UseRatebooksStateReturn {
  const gridAPIRef = useRef<MutableRefObject<GridApi<Ratebook> | null>>(null)
  const { updateSetting } = useUserSettings()
  const { useRatebooksQuery } = useRatebooks()
  const {
    exportPointToPointRates,
    importPointToPointRates,
    exportBaseFreight,
    importBaseFreight,
    exportSurcharges,
    importSurcharges,
  } = useRateManagement()
  const queryClient = useQueryClient()
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.freight_write

  const { data: ratebooks, isLoading } = useRatebooksQuery(type)

  // Format addresses for the grid if not already set
  useEffect(() => {
    if (ratebooks && ratebooks.length > 0) {
      // Create a local copy of the ratebooks to modify
      const updatedRatebooks = ratebooks.map((ratebook: Ratebook) => {
        // If we have address_info but no address, format it
        if (ratebook.address_info && (!ratebook.address || ratebook.address === '')) {
          // Format the address from address_info components
          const addressInfo = ratebook.address_info
          const parts = []
          if (addressInfo.address) parts.push(addressInfo.address)

          const cityState = []
          if (addressInfo.city) cityState.push(addressInfo.city)
          if (addressInfo.state) cityState.push(addressInfo.state)

          const cityStateStr = cityState.join(', ')
          if (cityStateStr) parts.push(cityStateStr)

          if (addressInfo.postal_code) parts.push(addressInfo.postal_code)

          // Set the formatted address
          return {
            ...ratebook,
            address: parts.join(', '),
          }
        }
        return ratebook
      })

      // If we made any changes, update the cache
      const hasChanges = updatedRatebooks.some(
        (updated: Ratebook, index: number) => updated.address !== ratebooks[index].address
      )

      if (hasChanges) {
        queryClient.setQueryData(['ratebooks', type], updatedRatebooks)
      }
    }
  }, [ratebooks, queryClient, type])

  // Base column definitions
  const baseColumnDefs: ColDef<Ratebook>[] = useMemo(
    () => [
      {
        headerName: 'Invoice Status',
        field: 'invoice_status',
        cellRenderer: Renderers.StatusRenderer,
        width: 120,
        minWidth: 120,
      },
      {
        headerName: `${type === 'customer' ? 'Customer' : 'Carrier'}`,
        field: 'counterparty_name',
        cellRenderer: Renderers.CustomerRenderer,
        flex: 2,
      },
      {
        headerName: 'Billing Volume Basis',
        field: 'invoice_configuration.billing_volume_basis',
        flex: 1,
      },
      {
        headerName: 'Billing Frequency',
        field: 'invoice_configuration.billing_frequency',
        flex: 1,
      },
      {
        headerName: 'Terms',
        field: 'invoice_configuration.invoice_terms.name',
        flex: 1,
      },
      {
        headerName: 'Invoice Grouping',
        field: 'invoice_configuration.invoice_grouping',
        flex: 1,
      },
      {
        headerName: 'Start Date',
        field: 'created_on',
        cellRenderer: Renderers.DateRenderer,
        width: 130,
        filter: 'agDateColumnFilter',
        filterParams: {
          browserDatePicker: true,
          comparator: (filterDate: Date, cellValue: Date) => {
            const filterDay = dayjs(filterDate).startOf('day')
            const cellDay = dayjs(cellValue).startOf('day')
            return cellDay.diff(filterDay, 'day')
          },
        },
      },
      {
        headerName: 'Split Load',
        field: 'freight_method',
        cellRenderer: Renderers.SplitLoadRenderer,
        flex: 1,
      },
      {
        headerName: 'Actions',
        field: '_id',
        cellRenderer: Renderers.ActionRenderer,
        width: 100,
        filter: false,
      },
    ],
    [type]
  )

  // Add the default transporter column for carrier type only
  const columnDefs = useMemo(() => {
    if (type === 'carrier') {
      return [
        // Use type assertion to handle the 'is_default_transporter' field
        {
          headerName: 'Default Transporter',
          field: 'is_default_transporter',
          cellRenderer: DefaultTransporterRenderer,
          minWidth: 100,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
        ...baseColumnDefs,
      ]
    }
    return baseColumnDefs
  }, [type, baseColumnDefs])

  // ag-grid prop overrides for carrier and customer pages
  const agPropOverrides = useMemo(() => {
    const baseOverrides = {
      rowSelection: 'multiple' as const,
      rowGroupPanelShow: 'always' as const,
      groupDisplayType: 'groupRows' as const,
      enableRangeSelection: true,
    }

    // Add carrier-specific props only for carrier type
    if (type === 'carrier') {
      return {
        ...baseOverrides,
        columnDefs: columnDefs as ColDef<Ratebook>[],
        // Apply row styling for default transporters
        getRowStyle: (params: RowClassParams) => {
          if (params.data?.is_default_transporter === true) {
            return { backgroundColor: 'var(--theme-warning-dim)' }
          }
          return {}
        },
      }
    }

    return baseOverrides
  }, [type, columnDefs])

  return {
    gridAPIRef,
    ratebooks,
    isLoading,
    updateSetting,
    canEdit,
    columnDefs,
    agPropOverrides,
    exportPointToPointRates,
    importPointToPointRates,
    exportBaseFreight,
    importBaseFreight,
    exportSurcharges,
    importSurcharges,
  }
}
